import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { HeaderRow, TH } from "@/components/ExpenseUi";
import { InfoHint } from "@/components/InfoHint";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { components } from "@dolfin/api-public";

type ExpenseResponse = components["schemas"]["ExpenseResponse"];
type ExpenseLineResponse = components["schemas"]["ExpenseLineResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];
type AccountingTaxRateResponse = components["schemas"]["AccountingTaxRateResponse"];

// "Automatic" sends no tax code: the backend picks the platform's rate from the line's VAT, the receipt and
// whether the VAT can be reclaimed - the same choice it makes for lines nobody has touched.
const AUTOMATIC = "__automatic__";

// Sales rates share the list on both platforms; an expense at one lands on the wrong VAT return box.
const SALES_RATE = /\b(output|income|sales?)\b/i;

// Rates an ordinary purchase never wants: capital goods, EC, reverse charge, imports. Still offered, at the
// bottom, so the everyday rates are the first thing a person sees.
const SPECIAL_RATE = /capital|\bcapex|\bec\b|ec |acquisition|reverse|\brc\b|import|postponed/i;

function rateOrder(r: AccountingTaxRateResponse) {
  const text = `${r.name ?? ""} ${r.code}`;
  return SPECIAL_RATE.test(text) ? 1 : 0;
}

interface DraftLine {
  key: string;
  ledgerAccountId: string | null;
  description: string;
  amount: string;
  taxAmount: string;
  taxCode: string | null;
}

let nextKey = 0;
const newKey = () => `line-${++nextKey}`;

function toNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

// The VAT a rate charges on a VAT-inclusive amount, which is what a card line is.
function vatAt(gross: number, ratePercent: number) {
  return round2((gross * ratePercent) / (100 + ratePercent));
}

function ratePercent(rate: AccountingTaxRateResponse | undefined): number {
  return rate?.rate == null ? 0 : Number(rate.rate);
}

function fromLine(line: ExpenseLineResponse): DraftLine {
  return {
    key: newKey(),
    ledgerAccountId: line.ledgerAccountId ?? null,
    description: line.description ?? "",
    amount: line.amount != null ? String(line.amount) : "",
    taxAmount: line.taxAmount != null ? String(line.taxAmount) : "",
    // A rate a person named earlier stays named. Every other line's rate was the automatic choice, and
    // opening it as Automatic means typing a VAT amount re-chooses it instead of being pinned to "No VAT".
    taxCode: line.source === "Manual" ? (line.taxCode ?? null) : null,
  };
}

export function ExpenseLinesEditor({
  expense,
  accounts,
  taxRates,
  onCancel,
  onSaved,
}: {
  expense: ExpenseResponse;
  accounts: LedgerAccountResponse[];
  /** The rates synced from the connected platform. Empty when none have been synced. */
  taxRates: AccountingTaxRateResponse[];
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const api = useDolfinClient();
  const total = Number(expense.amount ?? 0);
  const [lines, setLines] = useState<DraftLine[]>(() => {
    const existing = expense.lines ?? [];
    return existing.length > 0
      ? existing.map(fromLine)
      : [{ key: newKey(), ledgerAccountId: null, description: "", amount: String(total), taxAmount: "", taxCode: null }];
  });
  const [saving, setSaving] = useState(false);

  const purchaseRates = useMemo(
    () =>
      taxRates
        .filter((r) => r.isActive !== false && !SALES_RATE.test(`${r.name ?? ""} ${r.code}`))
        .sort(
          (a, b) =>
            rateOrder(a) - rateOrder(b) ||
            ratePercent(b) - ratePercent(a) ||
            (a.name ?? a.code).localeCompare(b.name ?? b.code),
        ),
    [taxRates],
  );
  const rateByCode = useMemo(() => new Map(taxRates.map((r) => [r.code, r])), [taxRates]);

  const linesTotal = lines.reduce((sum, l) => sum + (toNumber(l.amount) ?? 0), 0);
  const remaining = round2(total - linesTotal);
  const balanced = Math.abs(remaining) < 0.005;
  const missingAccount = lines.some((l) => !l.ledgerAccountId);
  const badNumber = lines.some(
    (l) => toNumber(l.amount) == null || (l.taxAmount.trim() !== "" && toNumber(l.taxAmount) == null),
  );
  const canSave = !saving && balanced && !missingAccount && !badNumber && lines.length > 0;

  function update(key: string, patch: Partial<DraftLine>) {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  // A named rate keeps the VAT in step with the amount, so a person picking "20%" does not also have to
  // work out the VAT. They can still overwrite it with the figure on the receipt.
  function changeAmount(line: DraftLine, amount: string) {
    const rate = line.taxCode ? rateByCode.get(line.taxCode) : undefined;
    const gross = toNumber(amount);
    if (rate && gross != null) {
      const pct = ratePercent(rate);
      update(line.key, { amount, taxAmount: pct > 0 ? String(vatAt(gross, pct)) : "" });
    } else {
      update(line.key, { amount });
    }
  }

  // VAT typed onto a line held at a zero or no-VAT rate means the rate is wrong, not the VAT: hand the line
  // back to Automatic, which picks the rate that charges it. Left named, the VAT would be ignored.
  function changeVat(line: DraftLine, taxAmount: string) {
    const vat = toNumber(taxAmount);
    const named = line.taxCode ? rateByCode.get(line.taxCode) : undefined;
    if (named && ratePercent(named) === 0 && vat != null && vat > 0) {
      update(line.key, { taxAmount, taxCode: null });
    } else {
      update(line.key, { taxAmount });
    }
  }

  function changeRate(line: DraftLine, value: string) {
    if (value === AUTOMATIC) {
      update(line.key, { taxCode: null });
      return;
    }
    const pct = ratePercent(rateByCode.get(value));
    const gross = toNumber(line.amount);
    update(line.key, {
      taxCode: value,
      taxAmount: pct > 0 && gross != null ? String(vatAt(gross, pct)) : "",
    });
  }

  // One rate for the whole expense: the common case on a single-rate receipt, without opening every line.
  function applyRateToAll(value: string) {
    setLines((current) =>
      current.map((l) => {
        if (value === AUTOMATIC) return { ...l, taxCode: null };
        const pct = ratePercent(rateByCode.get(value));
        const gross = toNumber(l.amount);
        return { ...l, taxCode: value, taxAmount: pct > 0 && gross != null ? String(vatAt(gross, pct)) : "" };
      }),
    );
  }

  // A new line takes whatever is left, which is what splitting usually means: lower one line, add another.
  function addLine() {
    setLines((current) => [
      ...current,
      {
        key: newKey(),
        ledgerAccountId: current[current.length - 1]?.ledgerAccountId ?? null,
        description: "",
        amount: remaining > 0 ? String(remaining) : "",
        taxAmount: "",
        taxCode: null,
      },
    ]);
  }

  async function save() {
    setSaving(true);
    const { error } = await api.PUT("/v1/expenses/{id}/lines", {
      params: { path: { id: expense.id! } },
      body: {
        lines: lines.map((l) => ({
          amount: toNumber(l.amount) ?? 0,
          ledgerAccountId: l.ledgerAccountId,
          description: l.description.trim() || null,
          taxCode: l.taxCode,
          taxAmount: toNumber(l.taxAmount),
        })),
      },
    });
    setSaving(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the lines."));
      return;
    }
    toast.success("Lines saved.");
    await onSaved();
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>Description</TableHead>
              <TableHead className={TH}>Account</TableHead>
              <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
              <TableHead className={cn(TH, "text-right")}>
                <span className="inline-flex items-center gap-1">
                  VAT
                  <InfoHint label="About the VAT amount">
                    The VAT included in this line's amount, as printed on the receipt. With the tax rate on
                    Automatic, the rate is chosen from this figure.
                  </InfoHint>
                </span>
              </TableHead>
              <TableHead className={TH}>Tax rate</TableHead>
              <TableHead className={TH}>
                <span className="sr-only">Remove</span>
              </TableHead>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => {
              const account = accounts.find((a) => a.id === line.ledgerAccountId);
              return (
                <TableRow key={line.key}>
                  <TableCell className="min-w-44">
                    <Input
                      value={line.description}
                      placeholder="What was bought"
                      className="h-8"
                      aria-label="Description"
                      onChange={(e) => update(line.key, { description: e.target.value })}
                    />
                  </TableCell>
                  <TableCell className="min-w-52">
                    <Select
                      value={line.ledgerAccountId ?? undefined}
                      onValueChange={(value) => update(line.key, { ledgerAccountId: value })}
                    >
                      <SelectTrigger size="sm" className="h-8 w-full" aria-label="Account">
                        <SelectValue placeholder="Choose an account" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* A line already on an account outside the spend list keeps showing it. */}
                        {line.ledgerAccountId && !account ? (
                          <SelectItem value={line.ledgerAccountId}>Current account</SelectItem>
                        ) : null}
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.id!}>
                            {a.displayId ? `${a.displayId} · ` : ""}
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="w-28">
                    <Input
                      value={line.amount}
                      inputMode="decimal"
                      className="h-8 text-right tabular-nums"
                      aria-label="Amount"
                      aria-invalid={toNumber(line.amount) == null}
                      onChange={(e) => changeAmount(line, e.target.value)}
                    />
                  </TableCell>
                  <TableCell className="w-24">
                    <Input
                      value={line.taxAmount}
                      inputMode="decimal"
                      placeholder="-"
                      className="h-8 text-right tabular-nums"
                      aria-label="VAT"
                      aria-invalid={line.taxAmount.trim() !== "" && toNumber(line.taxAmount) == null}
                      onChange={(e) => changeVat(line, e.target.value)}
                    />
                  </TableCell>
                  <TableCell className="min-w-48">
                    <Select value={line.taxCode ?? AUTOMATIC} onValueChange={(value) => changeRate(line, value)}>
                      <SelectTrigger size="sm" className="h-8 w-full" aria-label="Tax rate">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AUTOMATIC}>Automatic</SelectItem>
                        {/* A named sales rate from before stays visible rather than silently vanishing. */}
                        {line.taxCode && !purchaseRates.some((r) => r.code === line.taxCode) ? (
                          <SelectItem value={line.taxCode}>
                            {rateByCode.get(line.taxCode)?.name ?? line.taxCode}
                          </SelectItem>
                        ) : null}
                        {purchaseRates.map((r) => (
                          <SelectItem key={r.code} value={r.code}>
                            {r.name ?? r.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="w-10 text-right">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 text-muted-foreground hover:text-destructive"
                      aria-label="Remove line"
                      disabled={lines.length === 1}
                      onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {taxRates.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tax rates have been synced from your accounting platform yet, so every line uses Automatic. Sync
          the connection in Organisation, Accounting to choose rates here.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={addLine}>
            <Plus className="size-4" /> Add line
          </Button>
          {lines.length > 1 && purchaseRates.length > 0 ? (
            <Select value="" onValueChange={applyRateToAll}>
              <SelectTrigger size="sm" className="h-8 w-52" aria-label="Tax rate for every line">
                <SelectValue placeholder="Same rate for every line" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTOMATIC}>Automatic</SelectItem>
                {purchaseRates.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.name ?? r.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className={cn("text-muted-foreground", !balanced && "text-destructive")}>
            Lines total{" "}
            <span className="tabular-nums text-foreground">{formatCurrency(round2(linesTotal), expense.currency)}</span>{" "}
            of {formatCurrency(total, expense.currency)}
            {!balanced ? (
              <>
                {" "}
                ({remaining > 0 ? "short by" : "over by"}{" "}
                <span className="tabular-nums">{formatCurrency(Math.abs(remaining), expense.currency)}</span>)
              </>
            ) : null}
          </span>
          <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={!canSave} onClick={save}>
            {saving ? <Spinner className="size-4" /> : null} Save lines
          </Button>
        </div>
      </div>
      {missingAccount ? (
        <p className="text-right text-xs text-muted-foreground">Every line needs an account.</p>
      ) : null}
    </div>
  );
}
